import { Fragment, ReactNode } from "react";

import { Maybe } from "graphql/jsutils/Maybe";

import { MainAgentHeading } from "./MainAgentHeading";
import { MainContactHeading } from "./MainContactHeading";
import { CategorizedSpeakersComponents } from "./Title.decl";

import { SPEAKERS_COLORS } from "@/constants/style.constants";
import { categorizeSpeakers } from "@/helpers/participants/participants.helpers";
import { SpeakerWithChannel } from "@/helpers/participants/participants.helpers.decl";
import { Speaker } from "@/states/call/speakers";
import { SpeakerAvatar } from "../SpeakerAvatar/SpeakerAvatar";

/**
 * Joins two pieces of ReactNode with a separator.
 * If either of the two pieces is empty or null, it returns the non-empty part alone.
 * @param {ReactNode} element - The first element to join.
 * @param {ReactNode[]} elementArr - An array of elements to join.
 * @param {JSX.Element} separator - The JSX element used to separate the two pieces.
 * @returns {JSX.Element} - The joined JSX element.
 */

export function joinElements(
  element: ReactNode,
  elementArr: ReactNode[],
  separator: JSX.Element
): JSX.Element {
  if (element && elementArr.length) {
    return (
      <>
        {element} {separator} {elementArr}
      </>
    );
  }
  if (element) {
    return <>{element}</>;
  }
  return <>{elementArr}</>;
}

/**
 * Interleaves a separator between each node. Used to join multiple named
 * speakers with "and" when there is no single main speaker to anchor the
 * heading — `joinElements` only places a separator between its two arguments,
 * never within the array.
 */
export function joinNodes(
  nodes: ReactNode[],
  separator: JSX.Element
): ReactNode {
  return nodes.map((node, index) => (
    <Fragment key={`join_${index}`}>
      {index > 0 && separator}
      {node}
    </Fragment>
  ));
}

export function bindComponentsToSpeakers(
  speakers: Map<number, Speaker>,
  mainAgentId: Maybe<string>,
  mainExternalNumber: string
) {
  const categorizedSpeakers = categorizeSpeakers(
    speakers,
    mainAgentId,
    mainExternalNumber
  );

  return Object.entries(
    categorizedSpeakers
  ).reduce<CategorizedSpeakersComponents>(
    (accu, [category, value]) => {
      const _category = category as keyof ReturnType<typeof categorizeSpeakers>;

      if (_category === "mainAgent" && !!value) {
        const speaker = value as SpeakerWithChannel;
        accu.MainAgent = (
          <MainAgentHeading
            color={SPEAKERS_COLORS[speaker.variant]}
            fullName={speaker.fullName}
            variant={speaker.variant}
          />
        );

        return accu;
      }

      if (_category === "externalUser" && !!value) {
        const speaker = value as SpeakerWithChannel;

        if (!speaker?.number) {
          return accu;
        }

        accu.ExternalUser = (
          <MainContactHeading
            variant={speaker.variant}
            fullName={speaker.fullName}
            color={SPEAKERS_COLORS[speaker.variant]}
            number={speaker.number}
            companyName={speaker.companyName}
          />
        );

        return accu;
      }

      if (_category === "otherUsers") {
        const speakers = value as SpeakerWithChannel[];
        accu.OtherUsers = speakers.map((speaker) => {
          if (!speaker.channel) {
            return null;
          }

          return (
            <SpeakerAvatar
              key={`contact_${speaker.channel}`}
              channel={speaker.channel}
            />
          );
        });

        return accu;
      }

      if (_category === "otherAgents") {
        const speakers = value as SpeakerWithChannel[];
        const hasMainAgent = !!categorizedSpeakers.mainAgent;
        accu.OtherAgents = speakers.map((speaker) => {
          if (!speaker.channel) {
            return null;
          }

          // When no main agent is identified (e.g. inbound voice-agent calls
          // where `to` is the receiving line, not an agent), these agents are
          // the primary ones — render them with their names instead of bare
          // avatars so the header reads e.g. "Hina and Aman ... took a call".
          if (!hasMainAgent) {
            return (
              <MainAgentHeading
                key={`agent_${speaker.channel}`}
                color={SPEAKERS_COLORS[speaker.variant]}
                fullName={speaker.fullName}
                variant={speaker.variant}
                isVirtualAgent={speaker.isVirtualAgent}
              />
            );
          }

          return (
            <SpeakerAvatar
              key={`agent_${speaker.channel}`}
              channel={speaker.channel}
            />
          );
        });

        return accu;
      }

      return accu;
    },
    {
      MainAgent: null,
      ExternalUser: null,
      OtherAgents: [],
      OtherUsers: [],
    }
  );
}
